import { useState, useRef, useEffect } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getInitials, getAvatarColor } from '@/lib/displayUtils'
import { Check, ChevronDown, Search, User, X } from 'lucide-react'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
  online: boolean
}

interface AgentAssignmentDropdownProps {
  agents: Agent[]
  currentAgentId?: number
  selectedAgentId?: number | null
  onAssign: (agentId: number) => void
  onUnassign: () => void
  triggerVariant?: 'button' | 'inline'
}

export default function AgentAssignmentDropdown({
  agents,
  currentAgentId,
  selectedAgentId,
  onAssign,
  onUnassign,
  triggerVariant = 'button',
}: AgentAssignmentDropdownProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const filteredAgents = agents.filter(
    (a) =>
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.email.toLowerCase().includes(search.toLowerCase())
  )

  // Sort: current agent first, then alphabetical
  const sortedAgents = [...filteredAgents].sort((a, b) => {
    if (a.id === currentAgentId) return -1
    if (b.id === currentAgentId) return 1
    return a.name.localeCompare(b.name)
  })

  const selectedAgent = agents.find((a) => a.id === selectedAgentId)

  // Focus search input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 0)
    } else {
      setSearch('')
    }
  }, [open])

  const handleSelect = (agentId: number) => {
    onAssign(agentId)
    setOpen(false)
  }

  const handleUnassign = () => {
    onUnassign()
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerVariant === 'button' ? (
          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <User className="w-3.5 h-3.5" />
            Atribuir
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button className="w-full text-left group">
            <label className="text-xs text-gray-500">Atribuído a</label>
            <div className="mt-1 flex items-center gap-2 p-1.5 -mx-1.5 rounded-md hover:bg-gray-50 transition-colors cursor-pointer">
              {selectedAgent ? (
                <>
                  {selectedAgent.avatar_url ? (
                    <img
                      src={selectedAgent.avatar_url}
                      alt={selectedAgent.name}
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-xs ${getAvatarColor(
                        selectedAgent.name
                      )}`}
                    >
                      {getInitials(selectedAgent.name)}
                    </div>
                  )}
                  <span className="text-sm text-gray-700">{selectedAgent.name}</span>
                </>
              ) : (
                <span className="text-sm text-gray-400">Ninguém</span>
              )}
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar agente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto py-1">
          {/* Unassign option */}
          <button
            onClick={handleUnassign}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
              <X className="w-3 h-3 text-gray-400" />
            </div>
            <span>Ninguém</span>
            {selectedAgentId === null && (
              <Check className="w-4 h-4 text-blue-600 ml-auto" />
            )}
          </button>

          {sortedAgents.map((agent) => (
            <button
              key={agent.id}
              onClick={() => handleSelect(agent.id)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {agent.avatar_url ? (
                <img
                  src={agent.avatar_url}
                  alt={agent.name}
                  className="w-6 h-6 rounded-full object-cover shrink-0"
                />
              ) : (
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 ${getAvatarColor(
                    agent.name
                  )}`}
                >
                  {getInitials(agent.name)}
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <div className="truncate">
                  {agent.name}
                  {agent.id === currentAgentId && (
                    <span className="text-xs text-gray-400 ml-1">(você)</span>
                  )}
                </div>
              </div>
              {agent.id === selectedAgentId && (
                <Check className="w-4 h-4 text-blue-600 shrink-0" />
              )}
            </button>
          ))}

          {sortedAgents.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-400 text-center">
              Nenhum agente encontrado
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
